import React, { useState } from 'react';
import { Container, Row, Col, Form } from "react-bootstrap";
import {
  Phone,
  Mail,
  MessageCircle,
  Send,
  CheckCircle,
  MapPin,
  Clock,
  User,
  Globe,
  ChevronRight,
  Star,
  Shield
} from "react-feather";

const styles = {
  section: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    padding: '120px 0',
    position: 'relative',
    overflow: 'hidden',
  },
  title: {
    fontSize: '3.5rem',
    fontWeight: '800',
    color: '#1e293b',
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '1.2rem',
    color: '#64748b',
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 60px',
    lineHeight: '1.6',
  },
  contactCard: {
    backgroundColor: '#ffffff',
    borderRadius: '25px',
    padding: '50px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e2e8f0',
  },
  contactForm: {
    width: '100%',
  },
  formGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#475569',
  },
  input: {
    width: '100%',
    padding: '16px 20px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '1rem',
    color: '#1e293b',
    transition: 'all 0.3s ease',
    backgroundColor: '#f8fafc',
  },
  inputFocus: {
    outline: 'none',
    borderColor: '#3b82f6',
    backgroundColor: '#ffffff',
    boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
  },
  textarea: {
    width: '100%',
    padding: '16px 20px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '1rem',
    color: '#1e293b',
    transition: 'all 0.3s ease',
    backgroundColor: '#f8fafc',
    minHeight: '150px',
    resize: 'vertical',
  },
  submitButton: {
    background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
    border: 'none',
    color: '#ffffff',
    padding: '18px 45px',
    fontSize: '1.1rem',
    fontWeight: '600',
    borderRadius: '50px',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    cursor: 'pointer',
    marginTop: '10px',
  },
  submitButtonHover: {
    transform: 'translateY(-3px)',
    boxShadow: '0 15px 30px rgba(59, 130, 246, 0.4)',
  },
  contactInfo: {
    backgroundColor: 'rgba(59, 130, 246, 0.05)',
    borderRadius: '20px',
    padding: '40px 30px',
    height: '100%',
    border: '2px dashed rgba(59, 130, 246, 0.2)',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: '35px',
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '15px',
    border: '1px solid #e2e8f0',
    transition: 'all 0.3s ease',
  },
  contactItemHover: {
    transform: 'translateY(-5px)',
    borderColor: '#3b82f6',
    boxShadow: '0 10px 25px rgba(59, 130, 246, 0.1)',
  },
  contactIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '15px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  },
  contactTitle: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '8px',
  },
  contactValue: {
    fontSize: '1.1rem',
    color: '#3b82f6',
    fontWeight: '600',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
  },
  contactValueHover: {
    color: '#2563eb',
  },
  contactDescription: {
    fontSize: '0.9rem',
    color: '#64748b',
    marginTop: '5px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981',
    padding: '10px 25px',
    borderRadius: '20px',
    fontSize: '0.95rem',
    fontWeight: '600',
    marginBottom: '25px',
  },
  successMessage: {
    backgroundColor: '#d1fae5',
    border: '1px solid #10b981',
    borderRadius: '12px',
    padding: '20px',
    marginTop: '30px',
    textAlign: 'center',
    color: '#065f46',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontSize: '1rem',
    fontWeight: '500',
  },
  workingHours: {
    backgroundColor: 'rgba(245, 158, 11, 0.05)',
    borderRadius: '15px',
    padding: '25px',
    marginTop: '30px',
    border: '1px solid rgba(245, 158, 11, 0.2)',
  },
  hoursTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#92400e',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  hoursList: {
    listStyle: 'none',
    padding: '0',
    margin: '0',
  },
  hourItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid rgba(245, 158, 11, 0.1)',
    fontSize: '0.95rem',
    color: '#92400e',
  },
  hourDay: {
    fontWeight: '500',
  },
  hourTime: {
    fontWeight: '600',
  },
};

function Contact() {
  const [hoverStates, setHoverStates] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: "Phone Numbers",
      values: [
        { type: "Tunisia", value: "+216 51 679 495", link: "tel:+21651679495" },
        { type: "WhatsApp", value: "+33 7 46 43 22 88", link: "https://wa.me/33746432288" },
      ],
      color: '#3b82f6',
      description: "Call us directly or message on WhatsApp",
    },
    {
      icon: <Mail size={24} />,
      title: "Email Address",
      values: [
        { type: "Primary", value: "contact@nidhal.com", link: "mailto:www.nidhal.com@gmail.com" },
        { type: "Support", value: "support@vixo.com", link: "mailto:support@vixo.com" },
      ],
      color: '#10b981',
      description: "Get a response within 2 hours",
    },
    {
      icon: <MessageCircle size={24} />,
      title: "Social Media",
      values: [
        { type: "Instagram", value: "Instagram", link: "https://www.instagram.com/vixo.tn?igsh=cjRvZmtzMGhnbzg1&utm_source=qr" },
        { type: "Facebook", value: "Facebook", link: "https://www.facebook.com/people/Vixo/61587272546776/?mibextid=wwXIfr&rdid=TnCNJLjiY3Yh5vKl&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F182pS3r5zz%2F%3Fmibextid%3DwwXIfr" },
        { type: "LinkedIn", value: "LinkedIn", link: "https://linkedin.com/company/vixo" },
        { type: "Tiktok", value: "Tiktok", link: "https://tiktok.com/@vixoagency" },
        { type: "Twitter", value: "@vixoagency", link: "https://twitter.com/vixoagency" },
      ],
      color: '#8b5cf6',
      description: "Connect with us on professional networks",
    },
  ];

  const workingHours = [
    { day: "Monday - Friday", time: "8:00 AM - 8:00 PM" },
    { day: "Saturday", time: "9:00 AM - 5:00 PM" },
    { day: "Sunday", time: "10:00 AM - 4:00 PM" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    }, 3000);
  };

  return (
    <section id="contact" style={styles.section}>
      <Container>
        {/* Header */}
        <div className="text-center mb-5">
          <div style={styles.badge}>
            <Star size={16} />
            Let's Build Something Amazing Together
          </div>

          <h2 style={styles.title}>
            Ready to Transform Your <span style={{ color: '#3b82f6' }}>Business</span>?
          </h2>

          <p style={styles.subtitle}>
            Get in touch with our expert team. We'll help you create a customized
            solution that drives real results for your business.
          </p>
        </div>

        <Row className="g-5">
          {/* Contact Form */}
          <Col lg={7}>
            <div style={styles.contactCard}>
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: '10px',
                }}>
                  <User size={28} style={{ marginRight: '12px', verticalAlign: 'middle', color: '#3b82f6' }} />
                  Send us a Message
                </h3>
                <p style={{ color: '#64748b' }}>
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>
              </div>

              {submitted ? (
                <div style={styles.successMessage}>
                  <CheckCircle size={24} />
                  Thank you! Your message has been sent successfully.
                </div>
              ) : (
                <Form onSubmit={handleSubmit} style={styles.contactForm}>
                  <Row>
                    <Col md={6}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          style={styles.input}
                          onFocus={(e) => e.target.style = { ...styles.input, ...styles.inputFocus }}
                          onBlur={(e) => e.target.style = styles.input}
                          placeholder="John Doe"
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          style={styles.input}
                          onFocus={(e) => e.target.style = { ...styles.input, ...styles.inputFocus }}
                          onBlur={(e) => e.target.style = styles.input}
                          placeholder="john@example.com"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          style={styles.input}
                          onFocus={(e) => e.target.style = { ...styles.input, ...styles.inputFocus }}
                          onBlur={(e) => e.target.style = styles.input}
                          placeholder="+216 XX XXX XXX"
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Company Name</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          style={styles.input}
                          onFocus={(e) => e.target.style = { ...styles.input, ...styles.inputFocus }}
                          onBlur={(e) => e.target.style = styles.input}
                          placeholder="Your Company"
                        />
                      </div>
                    </Col>
                  </Row>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Your Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      style={styles.textarea}
                      onFocus={(e) => e.target.style = { ...styles.textarea, ...styles.inputFocus }}
                      onBlur={(e) => e.target.style = styles.textarea}
                      placeholder="Tell us about your project or inquiry..."
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: '2px solid #cbd5e1',
                      backgroundColor: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}>
                      <CheckCircle size={14} color="#10b981" />
                    </div>
                    <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
                      I agree to receive communications from VIXO
                    </span>
                  </div>

                  <button
                    type="submit"
                    style={{
                      ...styles.submitButton,
                      ...(hoverStates.submit ? styles.submitButtonHover : {})
                    }}
                    onMouseEnter={() => setHoverStates({ ...hoverStates, submit: true })}
                    onMouseLeave={() => setHoverStates({ ...hoverStates, submit: false })}
                  >
                    <Send size={20} />
                    Send Message
                    <ChevronRight size={20} />
                  </button>
                </Form>
              )}
            </div>
          </Col>

          {/* Contact Information */}
          <Col lg={5}>
            <div style={styles.contactInfo}>
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: '10px',
                }}>
                  <MapPin size={28} style={{ marginRight: '12px', verticalAlign: 'middle', color: '#3b82f6' }} />
                  Get in Touch
                </h3>
                <p style={{ color: '#64748b' }}>
                  Multiple ways to connect with our team
                </p>
              </div>

              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.contactItem,
                    ...(hoverStates[`info-${index}`] ? styles.contactItemHover : {})
                  }}
                  onMouseEnter={() => setHoverStates({ ...hoverStates, [`info-${index}`]: true })}
                  onMouseLeave={() => setHoverStates({ ...hoverStates, [`info-${index}`]: false })}
                >
                  <div style={{ ...styles.contactIcon, backgroundColor: `${info.color}15` }}>
                    <div style={{ color: info.color }}>
                      {info.icon}
                    </div>
                  </div>
                  <div style={{ flex: '1' }}>
                    <div style={styles.contactTitle}>{info.title}</div>
                    {info.values.map((item, idx) => (
                      <div key={idx} style={{ marginBottom: '5px' }}>
                        <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '2px' }}>
                          {item.type}:
                        </div>
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            ...styles.contactValue,
                            ...(hoverStates[`link-${index}-${idx}`] ? styles.contactValueHover : {})
                          }}
                          onMouseEnter={() => setHoverStates({ ...hoverStates, [`link-${index}-${idx}`]: true })}
                          onMouseLeave={() => setHoverStates({ ...hoverStates, [`link-${index}-${idx}`]: false })}
                        >
                          {item.value}
                        </a>
                      </div>
                    ))}
                    <div style={styles.contactDescription}>
                      {info.description}
                    </div>
                  </div>
                </div>
              ))}

              {/* Working Hours */}
              <div style={styles.workingHours}>
                <div style={styles.hoursTitle}>
                  <Clock size={18} />
                  Working Hours
                </div>
                <ul style={styles.hoursList}>
                  {workingHours.map((hour, index) => (
                    <li key={index} style={styles.hourItem}>
                      <span style={styles.hourDay}>{hour.day}</span>
                      <span style={styles.hourTime}>{hour.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Security Badge */}
              <div style={{
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '15px',
                padding: '20px',
                marginTop: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Shield size={24} color="#3b82f6" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>
                    Secure Communication
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    All your information is encrypted and protected
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Map Location */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          padding: '30px',
          marginTop: '60px',
          textAlign: 'center',
          border: '1px solid #e2e8f0',
        }}>
          <div style={{ marginBottom: '20px' }}>
            <Globe size={32} color="#3b82f6" style={{ marginBottom: '15px' }} />
            <h4 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1e293b' }}>
              Global Presence
            </h4>
            <p style={{ color: '#64748b', maxWidth: '600px', margin: '0 auto' }}>
              Serving clients worldwide from our headquarters in Tunisia with satellite offices in Europe
            </p>
          </div>
        </div>
      </Container>

      {/* Decorative Elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '5%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        zIndex: '0',
      }}></div>

      <div style={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
        zIndex: '0',
      }}></div>

      <style>
        {`
          @media (max-width: 768px) {
            .section {
              padding: 60px 0 !important;
            }
            
            .title {
              font-size: 2.5rem !important;
            }
            
            .contact-card {
              padding: 30px 20px !important;
            }
            
            .contact-info {
              padding: 30px 20px !important;
            }
          }
        `}
      </style>
    </section>
  );
}

export default Contact;